/**
 * Hold-to-pause and free dragging for ★ Reviews marquees.
 *
 * The track is a CSS animation, so rather than moving the element ourselves — which would
 * fight the animation — we scrub its currentTime: dragging N pixels is the same as jumping
 * N * msPerPixel milliseconds. The animation is linear and infinite, so wrapping the time
 * into a single iteration keeps the loop seamless however far the pointer travels; no
 * offset accumulates and no gap can open at the edges.
 *
 * Releasing clears the inline play state, which hands control back to the stylesheet: on
 * desktop the :hover rule keeps the track still until the pointer leaves, on touch there is
 * no hover so it starts scrolling again straight away.
 */

// Pointer travel before a drag starts, so a tap on a card is still a tap.
const DRAG_THRESHOLD = 3;

/**
 * @param {HTMLElement} marquee - a <marquee-component> marked with data-crm-drag
 */
function setup(marquee) {
  if (marquee.dataset.crmDragReady) return;
  marquee.dataset.crmDragReady = 'true';

  const wrapper = marquee.querySelector('.marquee__wrapper');
  if (!(wrapper instanceof HTMLElement)) return;

  // 'reverse' runs the keyframes backwards, so the track travels right as time grows and
  // the drag has to add time where a normal marquee subtracts it.
  const direction = marquee.dataset.movementDirection === 'reverse' ? -1 : 1;

  /** @type {Animation | null} */
  let animation = null;
  /** @type {number | null} */
  let pointerId = null;
  let startX = 0;
  let startTime = 0;
  let msPerPixel = 0;
  let duration = 0;
  let dragging = false;

  const iterationMs = (/** @type {Animation} */ current) => {
    const timing = current.effect?.getComputedTiming();
    return typeof timing?.duration === 'number' ? timing.duration : 0;
  };

  // One iteration walks the wrapper back by half its width plus half a gap (the keyframe in
  // the section), which is what turns pointer travel into animation time.
  const pixelsPerIteration = () => {
    const gap = parseFloat(getComputedStyle(wrapper).columnGap) || 0;
    return wrapper.offsetWidth / 2 + gap / 2;
  };

  const onPointerDown = (/** @type {PointerEvent} */ event) => {
    if (event.button > 0) return;

    const [current] = wrapper.getAnimations();
    // No animation means reduced motion, or styles that have not landed yet — nothing to drag.
    if (!current) return;

    duration = iterationMs(current);
    const distance = pixelsPerIteration();
    if (!duration || distance <= 0) return;

    animation = current;
    msPerPixel = duration / distance;
    pointerId = event.pointerId;
    startX = event.clientX;
    startTime = Number(animation.currentTime) || 0;
    dragging = false;

    // Inline play state beats the stylesheet, so the track stops under a finger too.
    wrapper.style.animationPlayState = 'paused';
  };

  const onPointerMove = (/** @type {PointerEvent} */ event) => {
    if (pointerId === null || event.pointerId !== pointerId || !animation) return;

    const delta = event.clientX - startX;

    if (!dragging) {
      if (Math.abs(delta) < DRAG_THRESHOLD) return;
      dragging = true;
      marquee.classList.add('is-dragging');
      marquee.setPointerCapture(pointerId);
    }

    const next = startTime - direction * delta * msPerPixel;
    animation.currentTime = ((next % duration) + duration) % duration;
  };

  const onPointerEnd = (/** @type {PointerEvent} */ event) => {
    if (pointerId === null || event.pointerId !== pointerId) return;

    if (marquee.hasPointerCapture(pointerId)) marquee.releasePointerCapture(pointerId);
    pointerId = null;
    dragging = false;
    animation = null;
    marquee.classList.remove('is-dragging');
    wrapper.style.animationPlayState = '';
  };

  // A drag over the cards is ours, never a native text/image drag or a text selection.
  // The stylesheet already says user-select: none; this is the belt to that pair of braces,
  // since a selection started here makes the whole strip unpleasant to swipe.
  marquee.addEventListener('dragstart', (event) => event.preventDefault());
  marquee.addEventListener('selectstart', (event) => event.preventDefault());

  marquee.addEventListener('pointerdown', onPointerDown);
  marquee.addEventListener('pointermove', onPointerMove);
  marquee.addEventListener('pointerup', onPointerEnd);
  marquee.addEventListener('pointercancel', onPointerEnd);
}

/**
 * @param {ParentNode} root
 */
function init(root) {
  for (const marquee of root.querySelectorAll('[data-crm-drag]')) {
    setup(/** @type {HTMLElement} */ (marquee));
  }
}

init(document);
document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof Element) init(event.target);
});
